import { useEffect, useRef, useState } from 'react';
import { LabeledInput } from './labeled-input';
import { handleException } from '../../shared/error-handler';

export const SearchAsYouType = ({
  label,
  value,
  disabled,
  error,
  onChange,
  onBlur,
  getSearchOptions,
  handleNewOptionsLoaded,
  inputRef,
  initialSearchInput,
  freeSolo = false,
  placeholder = 'Type to start searching...',
  defaultOptions = [],
  renderOption = null,
  isOptionEqualToValue = null,
  filterOptions = null,
  variant = null,
  helperText = null,
  getValidationError = null,
  updateOptions = false,
}) => {
  const isMounted = useRef(false);

  const [searchOptions, setSearchOptions] = useState([]);
  const [onBlurValue, setOnBlurValue] = useState();
  const [searchInput, setSearchInput] = useState('');
  const [initialSearchOptionsLoaded, setInitialSearchOptionsLoaded] =
    useState(false);

  // Runs initial search.
  useEffect(() => {
    if (!initialSearchOptionsLoaded && !searchInput && initialSearchInput) {
      setInitialSearchOptionsLoaded(true);
      setSearchInput(initialSearchInput);
    }
  }, [initialSearchInput]);

  // Load selected option.
  useEffect(() => {
    isMounted.current = true;

    if (!searchOptions.some((o) => o.value === value)) {
      loadNewOptions(value);
    }

    return () => {
      isMounted.current = false;
    };
  }, [value]);

  // Load new options upon search input change.
  useEffect(() => {
    isMounted.current = true;

    const delayedLoadOptionRequest = setTimeout(() => {
      loadNewOptions(searchInput);
    }, 500);

    return () => {
      clearTimeout(delayedLoadOptionRequest);
      isMounted.current = false;
    };
  }, [searchInput]);

  // Wait for search option loaded
  useEffect(() => {
    isMounted.current = true;

    const loadOptionsOnBlur = async () => {
      let selectedOption =
        searchOptions.find((o) => o.label === onBlurValue) ||
        findSelectedOption(onBlurValue, searchOptions);

      // If the selected option is not found or we need to update options, load new options
      if (!selectedOption || updateOptions) {
        const searchValue = selectedOption ? selectedOption.value : onBlurValue;
        const newOptions = await loadNewOptions(searchValue);
        selectedOption = findSelectedOption(searchValue, newOptions);
      }

      if (selectedOption) {
        onBlur?.({ target: { value: onBlurValue } }, selectedOption);
      }

      if (isMounted.current) {
        setOnBlurValue(undefined);
      }
    };

    if (onBlurValue) {
      loadOptionsOnBlur();
    }

    return () => {
      isMounted.current = false;
    };
  }, [onBlurValue]);

  const loadNewOptions = async (searchInput) => {
    const itemSelected =
      searchInput && searchOptions.some((o) => o.label === searchInput);

    const optionAlreadyLoaded = !searchInput || itemSelected;

    if (optionAlreadyLoaded) {
      return [];
    }

    try {
      const newOptions = await getSearchOptions(searchInput);

      if (newOptions && newOptions.length > 0 && isMounted.current) {
        setSearchOptions((existingOptions) => {
          const selectedOption = existingOptions.find((o) => o.value === value);
          const normalizedNewOptions = newOptions.map((option) =>
            option.value === value && selectedOption ? selectedOption : option
          );

          // Cache up to a certain amount of options
          for (const existingOption of existingOptions) {
            if (
              !normalizedNewOptions.some(
                (newOption) => newOption.value === existingOption.value
              )
            ) {
              normalizedNewOptions.push(existingOption);
            }
          }

          return normalizedNewOptions.slice(0, 50);
        });

        handleNewOptionsLoaded?.(newOptions);

        return newOptions;
      }
    } catch (e) {
      handleException(e);
    }

    return [];
  };

  const findSelectedOption = (value, options = []) => {
    let selectedOption = options.find(
      (o) =>
        (`${o.label}` || '').toUpperCase().trim() ===
          (`${value}` || '').toUpperCase().trim() || o.value === value
    );

    return selectedOption;
  };

  return (
    <LabeledInput
      type={'select-search'}
      label={label + ((onBlurValue && ` (loading "${onBlurValue}"...)`) || '')}
      value={value}
      options={
        defaultOptions?.length > 0 && searchOptions.length === 0
          ? defaultOptions
          : searchOptions
      }
      {...(isOptionEqualToValue && {
        isOptionEqualToValue,
      })}
      {...(filterOptions?.length && {
        filterOptions,
      })}
      onInputChange={(e, searchInput) => {
        setSearchInput(searchInput);
      }}
      renderOption={renderOption}
      freeSolo={freeSolo}
      placeholder={placeholder}
      {...(variant && { variant })}
      optionFilter={(x) => x}
      disabled={disabled}
      onChange={(e) =>
        onChange?.(
          e,
          findSelectedOption(
            e.target.value,
            defaultOptions?.length > 0 && searchOptions.length === 0
              ? defaultOptions
              : searchOptions
          )
        )
      }
      onBlur={(e) => setOnBlurValue(e.target.value)}
      inputRef={inputRef}
      error={error}
      helperText={helperText}
      getValidationError={getValidationError}
    />
  );
};
